/**
 * What a campaign's ledger statuses mean, named once for both ends.
 *
 * A recipient row holds ONE status, and the later facts overwrite the earlier
 * ones: a row that was `sent` becomes `bounced` when the provider says so, and
 * `replied` when the person writes back. That is the right shape for a ledger -
 * one row, one current truth - and it has a consequence that is easy to miss.
 *
 * ⚠⚠ `sent` DOES NOT MEAN "how many letters went out". It means "went out and
 * nothing has happened since". A campaign that mailed fifty people and heard
 * back from three shows `sent: 47`, and an operator reading that column as the
 * size of the send is reading a number that goes DOWN as the campaign succeeds.
 *
 * So anything that wants "how many letters left the building" sums the statuses
 * below instead. The API's delete guard already counts them from the ledger
 * (`counters.ts`, `contactedCount`); this module exists so the interface can
 * reach the same answer from the cached columns without keeping a second copy
 * of the list. A second copy is exactly what would drift on the day a status is
 * added, and the drift would show as a campaign that quietly looks smaller.
 *
 * Pure module. No clock, no DOM, no imports.
 */

/**
 * Statuses that mean a message actually left the building.
 *
 * `failed` is NOT here: the transport refused it, so nobody received anything.
 * `skipped` is not here either - a skipped row was screened out before a letter
 * existed, which is the whole point of screening.
 */
export const MKT_CONTACTED_STATUSES = ["sent", "bounced", "complained", "replied", "unsubscribed"] as const;

export type MktContactedStatus = (typeof MKT_CONTACTED_STATUSES)[number];

/** The counter columns the campaign list renders from. */
export interface MktCounters {
  sent: number;
  bounced: number;
  complained: number;
  replied: number;
  unsubscribed: number;
}

/**
 * How many addresses this campaign has actually written to.
 *
 * The number an operator means by "sent". Missing or non-numeric fields count
 * as zero rather than making the whole total `NaN`: a row from an older shape
 * should read as an undercount, not as an empty cell where a number belongs.
 */
export function mktContactedTotal(counters: Partial<MktCounters> | null | undefined): number {
  if (!counters) return 0;
  let total = 0;
  for (const status of MKT_CONTACTED_STATUSES) {
    const n = counters[status];
    if (typeof n === "number" && Number.isFinite(n)) total += n;
  }
  return total;
}

/**
 * What a MAILBOX's standing is, as opposed to one letter's.
 *
 * `MktContact.status` is the address's own record and it outlives every
 * campaign: an opt-out on Monday still stands for a campaign created in March.
 * The ledger statuses above describe one row of one campaign; these describe
 * the mailbox itself, and the two vocabularies are not interchangeable even
 * where the words overlap.
 *
 * ⚠ `blocked` is in the schema's documented vocabulary and NOTHING WRITES IT.
 * Measured 2026-08-22: the four writers are the default (`active`, restored
 * when a suppression entry is removed), the unsubscribe route and the manual
 * suppression route (`unsubscribed`), and the provider webhook (`bounced`,
 * `complained`). It is kept in the list anyway, because a screen that omits a
 * value the column may legally hold draws that row as something it is not the
 * day somebody starts writing it - which is a silent mislabel rather than a
 * crash.
 */
export const MKT_CONTACT_STATUSES = ["active", "unsubscribed", "bounced", "complained", "blocked"] as const;

export type MktContactStatus = (typeof MKT_CONTACT_STATUSES)[number];

/** Standings that mean this address is out of every future audience. `active`
 *  is the only one that is not: the suppression list holds an entry for each of
 *  the others, and the screener refuses on that entry rather than on this
 *  column. */
export const MKT_CONTACT_STOPPED: readonly MktContactStatus[] = [
  "unsubscribed",
  "bounced",
  "complained",
  "blocked",
];
